import { LLMProvider, LLMChatRequest, StreamCallbacks, LLMConfig, ToolSchema } from '../../types';

interface OllamaMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string;
  tool_name?: string;
  tool_calls?: Array<{
    function: {
      name: string;
      arguments: Record<string, unknown>;
    };
  }>;
}

interface OllamaStreamResponse {
  message?: {
    role: string;
    content: string;
    tool_calls?: Array<{
      function: {
        name: string;
        arguments: string;
      };
    }>;
  };
  done: boolean;
}

const convertToOllamaTools = (tools?: ToolSchema[]) => {
  if (!tools || tools.length === 0) return undefined;

  return tools.map((tool) => ({
    type: 'function' as const,
    function: {
      name: tool.name,
      description: tool.description,
      parameters: tool.parameters,
    },
  }));
};

const convertToOllamaMessages = (request: LLMChatRequest): OllamaMessage[] => {
  const messages: OllamaMessage[] = [];

  // Only add system message if systemPrompt exists and is non-empty
  if (request.systemPrompt && request.systemPrompt.trim()) {
    messages.push({ role: 'system', content: request.systemPrompt });
  }

  for (const msg of request.messages) {
    if (msg.role === 'user') {
      messages.push({ role: 'user', content: msg.content });
    } else if (msg.role === 'assistant') {
      const assistantMsg: OllamaMessage = { role: 'assistant', content: msg.content };
      if (msg.toolCalls && msg.toolCalls.length > 0) {
        assistantMsg.tool_calls = msg.toolCalls.map((tc) => ({
          function: {
            name: tc.name,
            arguments: tc.arguments,
          },
        }));
      }
      messages.push(assistantMsg);
    } else if (msg.role === 'tool' && msg.toolResult) {
      // Ollama requires tool_name for tool messages
      messages.push({
        role: 'tool',
        tool_name: msg.toolResult.name,
        content: JSON.stringify(msg.toolResult.result),
      });
    }
  }

  return messages;
};

export const createOllamaProvider = (config: LLMConfig): LLMProvider => {
  let abortController: AbortController | null = null;

  const chat = async (request: LLMChatRequest, callbacks: StreamCallbacks): Promise<void> => {
    abortController = new AbortController();

    try {
      const messages = convertToOllamaMessages(request);
      const tools = convertToOllamaTools(request.tools);

      // Validate that we have at least one message
      if (messages.length === 0) {
        throw new Error('At least one message is required for Ollama chat');
      }

      // Build request body - only include tools if provided (some models don't support them)
      const requestBody: Record<string, unknown> = {
        model: config.model,
        messages,
        stream: true,
        options: {
          temperature: config.temperature ?? 0.7,
          num_predict: config.maxTokens ?? 2048,
        },
      };

      // Only add tools if they exist (llama3:8b may not support function calling)
      if (tools && tools.length > 0) {
        requestBody.tools = tools;
      }

      const response = await fetch(`${config.baseUrl}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Ollama request failed: ${response.status} ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('No response body received');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read(); // eslint-disable-line no-await-in-loop
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;

          try {
            const json: OllamaStreamResponse = JSON.parse(line);

            if (json.message?.content) {
              fullContent += json.message.content;
              callbacks.onToken(json.message.content);
            }

            if (json.message?.tool_calls && json.message.tool_calls.length > 0) {
              for (const toolCall of json.message.tool_calls) {
                let args: Record<string, unknown>;
                try {
                  args =
                    typeof toolCall.function.arguments === 'string'
                      ? JSON.parse(toolCall.function.arguments)
                      : toolCall.function.arguments;
                } catch {
                  args = {};
                }

                callbacks.onToolCall({
                  id: `tc_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
                  name: toolCall.function.name,
                  arguments: args,
                });
              }
            }

            if (json.done) {
              break;
            }
          } catch {
            // Skip malformed JSON lines
          }
        }
      }

      callbacks.onComplete(fullContent);
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        return;
      }
      callbacks.onError(error as Error);
    } finally {
      abortController = null;
    }
  };

  const cancelRequest = (): void => {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  };

  return { chat, cancelRequest };
};
